/**
 * The palette's calculator (`=` mode): + - * / % ^, parentheses, unary minus,
 * decimal comma or point. A small recursive-descent parser — never eval.
 * Returns null for anything that is not a complete expression.
 */
export function calculate(input: string): number | null {
  const src = input.replace(/\s+/g, '');
  if (!src) return null;
  let pos = 0;

  const peek = () => src[pos];
  const eat = (ch: string) => {
    if (src[pos] === ch) {
      pos++;
      return true;
    }
    return false;
  };

  function number(): number | null {
    const m = /^(\d+(?:[.,]\d+)?|[.,]\d+)/.exec(src.slice(pos));
    if (!m) return null;
    pos += m[0].length;
    return Number(m[0].replace(',', '.'));
  }

  function primary(): number | null {
    if (eat('(')) {
      const v = sum();
      if (v === null || !eat(')')) return null;
      return v;
    }
    if (eat('-')) {
      const v = power();
      return v === null ? null : -v;
    }
    if (eat('+')) return power();
    return number();
  }

  function power(): number | null {
    const base = primary();
    if (base === null) return null;
    if (eat('^')) {
      const exp = power();
      return exp === null ? null : base ** exp;
    }
    return base;
  }

  function product(): number | null {
    let v = power();
    while (v !== null && (peek() === '*' || peek() === '/' || peek() === '%')) {
      const op = src[pos++];
      const r = power();
      if (r === null) return null;
      v = op === '*' ? v * r : op === '/' ? v / r : v % r;
    }
    return v;
  }

  function sum(): number | null {
    let v = product();
    while (v !== null && (peek() === '+' || peek() === '-')) {
      const op = src[pos++];
      const r = product();
      if (r === null) return null;
      v = op === '+' ? v + r : v - r;
    }
    return v;
  }

  const result = sum();
  if (result === null || pos !== src.length || !Number.isFinite(result)) return null;
  return result;
}

/** Formats a result for the given locale, without float noise. */
export function formatResult(value: number, locale = 'de'): string {
  return new Intl.NumberFormat(locale, { maximumFractionDigits: 10 }).format(
    Number(value.toPrecision(15))
  );
}
